import './LexicalContentEditor.css';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    LexicalComposer,
    type InitialConfigType
} from '@lexical/react/LexicalComposer';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {ListPlugin} from '@lexical/react/LexicalListPlugin';
import {LinkPlugin} from '@lexical/react/LexicalLinkPlugin';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$createHeadingNode, $createQuoteNode, HeadingNode, QuoteNode} from '@lexical/rich-text';
import {
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    ListItemNode,
    ListNode,
    REMOVE_LIST_COMMAND
} from '@lexical/list';
import {LinkNode, TOGGLE_LINK_COMMAND} from '@lexical/link';
import {TablePlugin} from '@lexical/react/LexicalTablePlugin';
import {
    TableCellNode,
    TableNode,
    TableRowNode,
    INSERT_TABLE_COMMAND,
    $createTableNodeWithDimensions,
    $isTableNode,
    $isTableRowNode,
    $isTableCellNode
} from '@lexical/table';
import {
    $createLineBreakNode,
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    $getSelection,
    $isElementNode,
    $isRangeSelection,
    COMMAND_PRIORITY_NORMAL,
    COMMAND_PRIORITY_LOW,
    FORMAT_TEXT_COMMAND,
    KEY_MODIFIER_COMMAND,
    EditorState,
    LexicalEditor,
    SELECTION_CHANGE_COMMAND,
    createCommand,
    DecoratorNode,
    type ElementNode,
    type LexicalNode,
    type NodeKey,
    type RangeSelection,
    type EditorConfig,
    type SerializedLexicalNode
} from 'lexical';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

import type {
    RichContentBlock,
    RichContentDocument,
    RichListItem,
    RichTextMark,
    RichTextSpan,
    RichTableRow,
    RichTableCell
} from '../../../api/blogTypes';
import {
    createEmptyContentDocument,
    pruneEmptyContentDocument,
    serializeContentDocumentToMarkdown
} from '../../../utils/blogContent';
import {$setBlocksType} from '@lexical/selection';

const theme = {
    paragraph: 'rte-paragraph',
    quote: 'rte-quote',
    link: 'rte-link',
    text: {
        bold: 'rte-bold',
        italic: 'rte-italic',
        underline: 'rte-underline',
        strikethrough: 'rte-strike',
        code: 'rte-code'
    },
    list: {
        nested: {
            listitem: 'rte-list-item'
        },
        listitem: 'rte-list-item',
        ol: 'rte-ol',
        ul: 'rte-ul'
    },
    heading: {
        h1: 'rte-heading rte-heading--h1',
        h2: 'rte-heading rte-heading--h2',
        h3: 'rte-heading rte-heading--h3',
        h4: 'rte-heading rte-heading--h4',
        h5: 'rte-heading rte-heading--h5',
        h6: 'rte-heading rte-heading--h6'
    },
    table: 'rte-table',
    tableRow: 'rte-table-row',
    tableCell: 'rte-table-cell',
    tableCellHeader: 'rte-table-cell-header'
};

type ImagePayload = {
    src: string;
    alt?: string | null;
    caption?: string | null;
};

const INSERT_IMAGE_COMMAND = createCommand<ImagePayload>('INSERT_IMAGE_COMMAND');

type Props = {
    value?: RichContentDocument | null;
    onChange: (doc: RichContentDocument, markdown: string) => void;
    onRequestImageUpload?: (
        file: File
    ) => Promise<{url?: string; src?: string; alt?: string | null; caption?: string | null}>;
    isImageUploading?: boolean;
    resolveImageSrc?: (filename: string) => string;
};

const preventFocusLoss = (event: React.MouseEvent) => {
    event.preventDefault();
};

function ToolbarButton({
                           icon,
                           label,
                           active,
                           onClick,
                           disabled
                       }: {
    icon: React.ReactNode;
    label: string;
    active?: boolean;
    disabled?: boolean;
    onClick: () => void;
}) {
    const className = `rich-content-editor__toolbar-button${active ? ' is-active' : ''}`;
    return (
        <button
            type="button"
            className={className}
            onMouseDown={preventFocusLoss}
            onClick={onClick}
            disabled={disabled}
            aria-pressed={active}
            title={label}
        >
            <span>{icon}</span>
        </button>
    );
}

function BlockTypeSelector({
                               current,
                               onSelect
                           }: {
    current: 'paragraph' | 'h2' | 'h3' | 'h4' | 'quote' | 'ul' | 'ol';
    onSelect: (block: 'paragraph' | 'h2' | 'h3' | 'h4' | 'quote') => void;
}) {
    return (
        <div className="rich-content-editor__heading-group">
            <ToolbarButton
                icon="P"
                label="Paragraph"
                active={current === 'paragraph'}
                onClick={() => onSelect('paragraph')}
            />
            <ToolbarButton
                icon="H2"
                label="Heading H2"
                active={current === 'h2'}
                onClick={() => onSelect('h2')}
            />
            <ToolbarButton
                icon="H3"
                label="Heading H3"
                active={current === 'h3'}
                onClick={() => onSelect('h3')}
            />
            <ToolbarButton
                icon="H4"
                label="Heading H4"
                active={current === 'h4'}
                onClick={() => onSelect('h4')}
            />
            <ToolbarButton
                icon="❝"
                label="Quote"
                active={current === 'quote'}
                onClick={() => onSelect('quote')}
            />
        </div>
    );
}

function getSelectedNode(selection: RangeSelection | null) {
    const anchor = selection?.anchor;
    const focus = selection?.focus;
    if (anchor && focus) {
        const anchorNode = anchor.getNode();
        const focusNode = focus.getNode();
        if (anchorNode === focusNode) {
            return anchorNode;
        }
        return selection?.isBackward() ? focusNode : anchorNode;
    }
    return null;
}

/** ------------------- Toolbar (editor via prop) ------------------- */
function Toolbar({
                     onInsertImage,
                     isImageUploading,
                     editor,
                 }: {
    onInsertImage?: () => void;
    isImageUploading?: boolean;
    editor: LexicalEditor;
}) {
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isStrike, setIsStrike] = useState(false);
    const [isCode, setIsCode] = useState(false);
    const [blockType, setBlockType] = useState<'paragraph' | 'h2' | 'h3' | 'h4' | 'quote' | 'ul' | 'ol'>(
        'paragraph'
    );

    useEffect(() => {
        const updateToolbar = (editorState = editor.getEditorState()) => {
            editorState.read(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) {
                    return;
                }
                const rangeSelection = selection;
                setIsBold(rangeSelection.hasFormat('bold'));
                setIsItalic(rangeSelection.hasFormat('italic'));
                setIsStrike(rangeSelection.hasFormat('strikethrough'));
                setIsCode(rangeSelection.hasFormat('code'));

                const anchorNode = rangeSelection.anchor.getNode();
                const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
                if (element instanceof ListNode) {
                    const type = element.getListType();
                    setBlockType(type === 'number' ? 'ol' : 'ul');
                } else {
                    const type = element.getType();
                    if (type === 'heading') {
                        if (element instanceof HeadingNode) {
                            const tag = element.getTag();
                            if (tag === 'h2' || tag === 'h3' || tag === 'h4') {
                                setBlockType(tag);
                                return;
                            }
                        }
                    }
                    if (type === 'quote') {
                        setBlockType('quote');
                        return;
                    }
                    setBlockType('paragraph');
                }
            });
        };

        updateToolbar();
        const unregisterSelection = editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            () => {
                updateToolbar();
                return false;
            },
            COMMAND_PRIORITY_LOW
        );
        const unregisterUpdate = editor.registerUpdateListener(({editorState}) => {
            updateToolbar(editorState);
        });
        return () => {
            unregisterSelection();
            unregisterUpdate();
        };
    }, [editor]);

    const toggleBlock = useCallback(
        (next: 'paragraph' | 'h2' | 'h3' | 'h4' | 'quote') => {
            editor.update(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) {
                    return;
                }
                const rangeSelection = selection;
                switch (next) {
                    case 'paragraph':
                        $setBlocksType(rangeSelection, () => $createParagraphNode());
                        break;
                    case 'quote':
                        $setBlocksType(rangeSelection, () => $createQuoteNode());
                        break;
                    case 'h2':
                    case 'h3':
                    case 'h4':
                        $setBlocksType(rangeSelection, () => $createHeadingNode(next));
                        break;
                }
            });
        },
        [editor]
    );

    const toggleList = useCallback(
        (type: 'ol' | 'ul') => {
            editor.update(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) {
                    return;
                }
                const rangeSelection = selection;
                const anchorNode = rangeSelection.anchor.getNode();
                const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
                const targetType = type === 'ol' ? 'number' : 'bullet';
                if (element instanceof ListNode && element.getListType() === targetType) {
                    editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
                } else {
                    editor.dispatchCommand(
                        type === 'ol' ? INSERT_ORDERED_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
                        undefined
                    );
                }
            });
        },
        [editor]
    );

    const handleToggleLink = useCallback(() => {
        let url: string | null = null;
        editor.getEditorState().read(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) {
                return;
            }
            const node = getSelectedNode(selection);
            const parent = node ? node.getParent() : null;
            if (parent instanceof LinkNode) {
                url = parent.getURL();
            }
        });

        const nextUrl = window.prompt('Enter the link URL', url ?? 'https://');
        if (nextUrl === null) {
            return;
        }
        const trimmed = nextUrl.trim();
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, trimmed.length > 0 ? trimmed : null);
    }, [editor]);

    return (
        <div className="rich-content-editor__toolbar" role="toolbar" aria-label="Text formatting">
            <div className="rich-content-editor__toolbar-group">
                <ToolbarButton
                    icon={<strong>B</strong>}
                    label="Bold"
                    active={isBold}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
                />
                <ToolbarButton
                    icon={<em>I</em>}
                    label="Italic"
                    active={isItalic}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
                />
                <ToolbarButton
                    icon={<span className="rich-content-editor__toolbar-icon">S</span>}
                    label="Strikethrough"
                    active={isStrike}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
                />
                <ToolbarButton
                    icon={'</>'}
                    label="Monospace"
                    active={isCode}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
                />
            </div>

            <div className="rich-content-editor__toolbar-divider" aria-hidden="true" />

            <BlockTypeSelector current={blockType} onSelect={toggleBlock} />

            <div className="rich-content-editor__toolbar-divider" aria-hidden="true" />

            <div className="rich-content-editor__toolbar-group">
                <ToolbarButton
                    icon="•"
                    label="Bulleted list"
                    active={blockType === 'ul'}
                    onClick={() => toggleList('ul')}
                />
                <ToolbarButton
                    icon="1."
                    label="Numbered list"
                    active={blockType === 'ol'}
                    onClick={() => toggleList('ol')}
                />
            </div>

            <div className="rich-content-editor__toolbar-divider" aria-hidden="true" />

            <ToolbarButton icon="🔗" label="Link" onClick={handleToggleLink} />

            <div className="rich-content-editor__toolbar-divider" aria-hidden="true" />

            <div className="rich-content-editor__toolbar-group">
                {onInsertImage && (
                    <ToolbarButton
                        icon={isImageUploading ? '…' : '🖼️'}
                        label="Insert image"
                        onClick={onInsertImage}
                        disabled={isImageUploading}
                    />
                )}
                <ToolbarButton
                    icon="⊞"
                    label="Insert table"
                    onClick={() => {
                        editor.dispatchCommand(INSERT_TABLE_COMMAND, {rows: "3", columns: "3"});
                    }}
                />
            </div>
        </div>
    );
}

function Placeholder() {
    return <div className="rich-content-editor__placeholder">Start writing here…</div>;
}

function applyMarksToTextNode(node: ReturnType<typeof $createTextNode>, marks?: RichTextMark[]) {
    if (!marks) {
        return;
    }
    marks.forEach((mark) => {
        if (mark === 'bold') {
            node.toggleFormat('bold');
        }
        if (mark === 'italic') {
            node.toggleFormat('italic');
        }
        if (mark === 'strike') {
            node.toggleFormat('strikethrough');
        }
        if (mark === 'code') {
            node.toggleFormat('code');
        }
    });
}

function appendSpan(parent: ElementNode, span: RichTextSpan) {
    const marks = span.marks ?? [];
    const parts = span.text.split('\n');
    const createText = (text: string) => {
        const textNode = $createTextNode(text);
        applyMarksToTextNode(textNode, marks.filter((mark) => mark !== 'link' && mark !== 'anchor'));
        return textNode;
    };

    const appendTo = (container: ElementNode) => {
        parts.forEach((part, index) => {
            container.append(createText(part));
            if (index < parts.length - 1) {
                container.append($createLineBreakNode());
            }
        });
    };

    if ((marks.includes('link') || marks.includes('anchor')) && span.href) {
        const linkNode = new LinkNode(span.href);
        appendTo(linkNode);
        parent.append(linkNode);
    } else {
        appendTo(parent);
    }
}

function importBlock(block: RichContentBlock, root: ReturnType<typeof $getRoot>) {
    switch (block.type) {
        case 'heading': {
            const level = Math.min(4, Math.max(2, block.level ?? 2));
            const tag = level === 2 ? 'h2' : level === 3 ? 'h3' : 'h4';
            const heading = $createHeadingNode(tag);
            if (block.children.length === 0) {
                heading.append($createTextNode(''));
            } else {
                block.children.forEach((span) => appendSpan(heading, span));
            }
            root.append(heading);
            break;
        }
        case 'quote': {
            const quote = $createQuoteNode();
            if (block.children.length === 0) {
                quote.append($createTextNode(''));
            } else {
                block.children.forEach((span) => appendSpan(quote, span));
            }
            root.append(quote);
            break;
        }
        case 'list': {
            const listNode = new ListNode(block.style === 'ordered' ? 'number' : 'bullet');
            const items = block.items.length > 0 ? block.items : [{children: [{text: ''}]} as RichListItem];
            items.forEach((item) => {
                const itemNode = new ListItemNode();
                if (item.children.length === 0) {
                    itemNode.append($createTextNode(''));
                } else {
                    item.children.forEach((span) => appendSpan(itemNode, span));
                }
                listNode.append(itemNode);
            });
            root.append(listNode);
            break;
        }
        case 'image': {
            const imageNode = new BlogImageNode(block.src, block.alt ?? null, block.caption ?? null);
            root.append(imageNode);
            break;
        }
        case 'table': {
            const tableNode = new TableNode();
            block.rows.forEach((row) => {
                const rowNode = new TableRowNode();
                row.cells.forEach((cell) => {
                    const cellNode = new TableCellNode(cell.type === 'header' ? 1 : 0);
                    if (cell.children.length === 0) {
                        const p = $createParagraphNode();
                        p.append($createTextNode(''));
                        cellNode.append(p as any);
                    } else {
                        const p = $createParagraphNode();
                        cell.children.forEach((span) => appendSpan(p, span));
                        cellNode.append(p as any);
                    }
                    rowNode.append(cellNode as any);
                });
                tableNode.append(rowNode as any);
            });
            root.append(tableNode as any);
            break;
        }
        case 'paragraph':
        default: {
            const paragraph = $createParagraphNode();
            if (block.children.length === 0) {
                paragraph.append($createTextNode(''));
            } else {
                block.children.forEach((span) => appendSpan(paragraph, span));
            }
            root.append(paragraph);
        }
    }
}

function importDocument(editor: LexicalEditor, document: RichContentDocument) {
    editor.update(() => {
        const root = $getRoot();
        root.clear();
        if (!document.blocks.length) {
            const paragraph = $createParagraphNode();
            paragraph.append($createTextNode(''));
            root.append(paragraph);
            return;
        }
        document.blocks.forEach((block) => importBlock(block, root));
    });
}

function collectSpansFromNode(node: LexicalNode): RichTextSpan[] {
    const spans: RichTextSpan[] = [];
    let buffer: {text: string; marks?: RichTextMark[]; href?: string | null} = {text: ''};

    const flush = () => {
        if (!buffer.text) {
            return;
        }
        const marks = buffer.marks ? [...buffer.marks] : undefined;
        if (buffer.href) {
            const marksWithLink = marks ? [...marks] : [];
            if (!marksWithLink.includes('link')) {
                marksWithLink.push('link');
            }
            spans.push({text: buffer.text, marks: marksWithLink, href: buffer.href});
        } else {
            spans.push({text: buffer.text, marks});
        }
        buffer = {text: ''};
    };

    const pushText = (text: string, marks: RichTextMark[] = [], href?: string | null) => {
        const normalizedMarks = marks.length ? marks : undefined;
        const sameMarks = JSON.stringify(buffer.marks ?? null) === JSON.stringify(normalizedMarks ?? null);
        const sameHref = buffer.href === (href ?? null);
        if (!sameMarks || !sameHref) {
            flush();
            buffer.marks = normalizedMarks;
            buffer.href = href ?? undefined;
        }
        buffer.text += text;
    };

    const visit = (current: LexicalNode, activeHref?: string | null) => {
        const type = current.getType?.();
        if (type === 'linebreak') {
            pushText('\n', buffer.marks as RichTextMark[] | undefined, activeHref);
            return;
        }

        if (current instanceof LinkNode) {
            const url = current.getURL();
            current.getChildren().forEach((child) => visit(child, url));
            return;
        }

        if (type === 'text') {
            const textContent = current.getTextContent();
            const marks: RichTextMark[] = [];
            if ((current as any).hasFormat?.('bold')) {
                marks.push('bold');
            }
            if ((current as any).hasFormat?.('italic')) {
                marks.push('italic');
            }
            if ((current as any).hasFormat?.('strikethrough')) {
                marks.push('strike');
            }
            if ((current as any).hasFormat?.('code')) {
                marks.push('code');
            }
            pushText(textContent, marks, activeHref ?? undefined);
            return;
        }

        if ($isElementNode(current)) {
            const children = current.getChildren();
            children.forEach((child) => visit(child, activeHref));
        }
    };

    visit(node);
    flush();
    return spans;
}

function exportDocument(editor: LexicalEditor): RichContentDocument {
    const blocks: RichContentBlock[] = [];
    editor.getEditorState().read(() => {
        const root = $getRoot();
        root.getChildren().forEach((node) => {
            if (node instanceof BlogImageNode) {
                blocks.push({
                    type: 'image' as const,
                    src: node.getSrc(),
                    alt: node.getAlt() ?? null,
                    caption: node.getCaption() ?? null
                });
                return;
            }

            if (node instanceof ListNode) {
                const style = node.getListType() === 'number' ? 'ordered' : 'unordered';
                const items: RichListItem[] = node.getChildren().map((child) => ({
                    children: collectSpansFromNode(child)
                }));
                blocks.push({type: 'list', style, items});
                return;
            }

            if ($isTableNode(node as any)) {
                const rows: RichTableRow[] = [];
                (node as any).getChildren().forEach((rowNode: any) => {
                    if ($isTableRowNode(rowNode)) {
                        const cells: RichTableCell[] = [];
                        rowNode.getChildren().forEach((cellNode: any) => {
                            if ($isTableCellNode(cellNode)) {
                                const isHeader = cellNode.getHeaderStyles() !== 0;
                                cells.push({
                                    type: isHeader ? 'header' : 'cell',
                                    children: collectSpansFromNode(cellNode as any)
                                });
                            }
                        });
                        rows.push({cells});
                    }
                });
                blocks.push({type: 'table', rows});
                return;
            }

            const type = node.getType();
            if (node instanceof HeadingNode) {
                const tag = node.getTag();
                const level = tag === 'h3' ? 3 : tag === 'h4' ? 4 : 2;
                blocks.push({
                    type: 'heading',
                    level,
                    children: collectSpansFromNode(node)
                });
                return;
            }

            if (type === 'quote') {
                blocks.push({
                    type: 'quote',
                    children: collectSpansFromNode(node)
                });
                return;
            }

            blocks.push({
                type: 'paragraph',
                children: collectSpansFromNode(node)
            });
        });
    });
    return {blocks};
}

/** ------------------- Image insertion hook (editor via arg) ------------------- */
function createImageInsertion(
    editor: LexicalEditor,
    onRequestImageUpload: Props['onRequestImageUpload'],
    isImageUploading: Props['isImageUploading']
) {
    return async () => {
        if (!onRequestImageUpload || isImageUploading) {
            return;
        }
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.style.display = 'none';
        document.body.appendChild(input);
        const cleanup = () => {
            input.value = '';
            input.onchange = null;
            if (input.parentNode) input.parentNode.removeChild(input);
        };
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return cleanup();
            try {
                const uploaded = await onRequestImageUpload(file);
                const src = uploaded?.src ?? uploaded?.url;
                if (!src) return cleanup();
                const alt = uploaded?.alt ?? null;
                const caption = uploaded?.caption ?? null;
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {src, alt, caption});
            } finally {
                cleanup();
            }
        };
        input.click();
    };
}

type SerializedBlogImageNode = SerializedLexicalNode & {
    type: 'blog-image';
    version: 1;
    src: string;
    alt: string | null;
    caption: string | null;
};

class BlogImageNode extends DecoratorNode<React.ReactNode> {
    __src: string;
    __alt: string | null;
    __caption: string | null;

    static __resolveImageSrc: ((src: string) => string) | null = null;

    static getType(): string {
        return 'blog-image';
    }

    static clone(node: BlogImageNode): BlogImageNode {
        return new BlogImageNode(node.__src, node.__alt, node.__caption, node.getKey());
    }

    static importJSON(serialized: SerializedBlogImageNode): BlogImageNode {
        return new BlogImageNode(serialized.src, serialized.alt ?? null, serialized.caption ?? null);
    }

    static setImageSrcResolver(resolver?: (src: string) => string) {
        BlogImageNode.__resolveImageSrc = resolver ?? null;
    }

    static resolveImageSrc(raw: string): string {
        const resolver = BlogImageNode.__resolveImageSrc;
        if (resolver) {
            return resolver(raw);
        }
        if (/^https?:\/\//i.test(raw) || raw.startsWith('//')) {
            return raw;
        }
        return raw;
    }

    exportJSON(): SerializedBlogImageNode {
        return {
            type: 'blog-image',
            version: 1,
            src: this.__src,
            alt: this.__alt,
            caption: this.__caption
        };
    }

    constructor(src: string, alt?: string | null, caption?: string | null, key?: NodeKey) {
        super(key);
        this.__src = src;
        this.__alt = alt ?? null;
        this.__caption = caption ?? null;
    }

    getSrc(): string {
        return this.__src;
    }

    getAlt(): string | null {
        return this.__alt;
    }

    getCaption(): string | null {
        return this.__caption;
    }

    createDOM(_config: EditorConfig): HTMLElement {
        const dom = document.createElement('div');
        dom.className = 'rich-content-editor__image';
        return dom;
    }

    updateDOM(_prevNode: BlogImageNode, _dom: HTMLElement, _config: EditorConfig): boolean {
        return false;
    }

    decorate(_editor: LexicalEditor, _config: EditorConfig): React.ReactNode {
        const resolvedSrc = BlogImageNode.resolveImageSrc(this.__src);
        return (
            <figure className="rich-content-editor__image-figure">
                <img src={resolvedSrc} alt={this.__alt ?? undefined} loading="lazy" />
                {(this.__caption ?? '').trim().length > 0 && (
                    <figcaption>{this.__caption}</figcaption>
                )}
            </figure>
        );
    }
}

/** ------------------- ImagesPlugin (editor via prop) ------------------- */
function ImagesPlugin({editor}:{editor: LexicalEditor}) {
    useEffect(() => {
        return editor.registerCommand<ImagePayload>(
            INSERT_IMAGE_COMMAND,
            (payload) => {
                editor.update(() => {
                    const imageNode = new BlogImageNode(payload.src, payload.alt ?? null, payload.caption ?? null);
                    const selection = $getSelection();

                    if ($isRangeSelection(selection)) {
                        const anchorNode = selection.anchor.getNode();
                        const topLevelElement = anchorNode.getKey() === 'root'
                            ? anchorNode
                            : anchorNode.getTopLevelElementOrThrow();

                        topLevelElement.insertAfter(imageNode);

                        const newParagraph = $createParagraphNode();
                        newParagraph.append($createTextNode(''));
                        imageNode.insertAfter(newParagraph);
                        newParagraph.select();
                    } else {
                        $getRoot().append(imageNode);
                        const paragraph = $createParagraphNode();
                        paragraph.append($createTextNode(''));
                        imageNode.insertAfter(paragraph);
                        paragraph.select();
                    }
                });
                return true;
            },
            COMMAND_PRIORITY_NORMAL
        );
    }, [editor]);
    return null;
}

/** ------------------- HotkeysPlugin (editor via prop) ------------------- */
function HotkeysPlugin({editor}:{editor: LexicalEditor}) {
    useEffect(() => {
        return editor.registerCommand<KeyboardEvent>(
            KEY_MODIFIER_COMMAND,
            (event) => {
                const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
                const isMod = isMac ? event.metaKey : event.ctrlKey;
                if (!isMod) {
                    return false;
                }
                switch (event.key.toLowerCase()) {
                    case 'b':
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
                        event.preventDefault();
                        return true;
                    case 'i':
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
                        event.preventDefault();
                        return true;
                    case 's':
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
                        event.preventDefault();
                        return true;
                    case 'k': {
                        const selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            const node = getSelectedNode(selection);
                            const parent = node ? node.getParent() : null;
                            const current = parent instanceof LinkNode ? parent.getURL() : 'https://';
                            const next = window.prompt('Enter the link URL', current ?? 'https://');
                            if (next !== null) {
                                const trimmed = next.trim();
                                editor.dispatchCommand(TOGGLE_LINK_COMMAND, trimmed.length > 0 ? trimmed : null);
                            }
                        }
                        event.preventDefault();
                        return true;
                    }
                    default:
                        return false;
                }
            },
            COMMAND_PRIORITY_LOW
        );
    }, [editor]);
    return null;
}

/**
 * SyncPlugin - imports document ONLY on initial mount.
 * After the first import, the editor is the source of truth.
 * This prevents the bug where saving/reloading would overwrite newly added images.
 */
function SyncPlugin({
                        initialDocument,
                        editor
                    }: {
    initialDocument: RichContentDocument;
    editor: LexicalEditor;
}) {
    const mountedRef = useRef(false);

    useEffect(() => {
        // Import ONLY on first mount - never re-import from props
        if (!mountedRef.current) {
            mountedRef.current = true;
            importDocument(editor, initialDocument);
        }
        // intentionally NOT including initialDocument in deps - we only want first mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

    return null;
}

/** ------------------- EditorRefPlugin (внутри Composer) ------------------- */
function EditorRefPlugin({onReady}:{onReady:(ed: LexicalEditor)=>void}) {
    const [editor] = useLexicalComposerContext();
    useEffect(() => { onReady(editor); }, [editor, onReady]);
    return null;
}

const BlogImageNodes = [BlogImageNode];

const initialConfigBase: InitialConfigType = {
    namespace: 'blog-post-editor',
    theme,
    onError(error) {
        console.error(error);
    },
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, TableNode as any, TableRowNode as any, TableCellNode as any, ...BlogImageNodes]
};

export default function LexicalContentEditor({
                                                 value,
                                                 onChange,
                                                 onRequestImageUpload,
                                                 isImageUploading,
                                                 resolveImageSrc
                                             }: Props) {
    // Store initial document at mount time - this is the only time we read from props
    const initialDocumentRef = useRef<RichContentDocument | null>(null);
    if (initialDocumentRef.current === null) {
        initialDocumentRef.current = value ?? createEmptyContentDocument();
    }
    const initialDocument = initialDocumentRef.current;

    const handleEditorChange = useCallback(
        (_editorState: EditorState, editor: LexicalEditor) => {
            const exported = exportDocument(editor);
            const normalized = pruneEmptyContentDocument(exported);
            const markdown = serializeContentDocumentToMarkdown(normalized);
            onChange(normalized, markdown);
        },
        [onChange]
    );

    const [editor, setEditor] = useState<LexicalEditor | null>(null);

    const initialConfig = useMemo(() => initialConfigBase, []);

    const handleInsertImage = useMemo(() => {
        if (!editor) return undefined;
        return createImageInsertion(editor, onRequestImageUpload, isImageUploading);
    }, [editor, onRequestImageUpload, isImageUploading]);

    useEffect(() => {
        BlogImageNode.setImageSrcResolver(resolveImageSrc);
        return () => BlogImageNode.setImageSrcResolver(undefined);
    }, [resolveImageSrc]);

    return (
        <div className="rich-content-editor">
            <LexicalComposer initialConfig={initialConfig}>
                <EditorRefPlugin onReady={setEditor} />

                {editor && (
                    <Toolbar
                        editor={editor}
                        onInsertImage={onRequestImageUpload ? handleInsertImage : undefined}
                        isImageUploading={isImageUploading}
                    />
                )}

                <div className="rich-content-editor__surface">
                    <RichTextPlugin
                        contentEditable={
                            <div className="rich-content-editor__inner">
                                <ContentEditable className="rich-content-editor__content" />
                            </div>
                        }
                        placeholder={<Placeholder />}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <HistoryPlugin />
                    <ListPlugin />
                    <LinkPlugin />
                    <TablePlugin />

                    {editor && <ImagesPlugin editor={editor} />}
                    {editor && <HotkeysPlugin editor={editor} />}

                    <OnChangePlugin onChange={handleEditorChange} />

                    {editor && (
                        <SyncPlugin
                            editor={editor}
                            initialDocument={initialDocument}
                        />
                    )}
                </div>
            </LexicalComposer>
        </div>
    );
}
